import { useTranslation } from 'react-i18next'
import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import type { WorkflowPattern } from '@/schemas/workflow'

interface PatternSelectorProps {
  onSelect: (pattern: WorkflowPattern) => void
  selected?: WorkflowPattern
}

const PATTERNS: {
  value: WorkflowPattern
  icon: React.ReactNode
}[] = [
  {
    value: 'chaining',
    icon: (
      <svg viewBox="0 0 120 40" className="w-full h-10" fill="none">
        <circle cx="20" cy="20" r="8" className="fill-indigo-500/30 stroke-indigo-400" strokeWidth="1.5" />
        <text x="20" y="24" textAnchor="middle" className="fill-indigo-300 text-[10px]">A</text>
        <line x1="28" y1="20" x2="52" y2="20" className="stroke-indigo-400/60" strokeWidth="1.5" markerEnd="url(#arrow)" />
        <circle cx="60" cy="20" r="8" className="fill-indigo-500/30 stroke-indigo-400" strokeWidth="1.5" />
        <text x="60" y="24" textAnchor="middle" className="fill-indigo-300 text-[10px]">B</text>
        <line x1="68" y1="20" x2="92" y2="20" className="stroke-indigo-400/60" strokeWidth="1.5" markerEnd="url(#arrow)" />
        <circle cx="100" cy="20" r="8" className="fill-indigo-500/30 stroke-indigo-400" strokeWidth="1.5" />
        <text x="100" y="24" textAnchor="middle" className="fill-indigo-300 text-[10px]">C</text>
        <defs>
          <marker id="arrow" markerWidth="6" markerHeight="4" refX="5" refY="2" orient="auto">
            <path d="M0,0 L6,2 L0,4" className="fill-indigo-400/60" />
          </marker>
        </defs>
      </svg>
    ),
  },
  {
    value: 'parallel',
    icon: (
      <svg viewBox="0 0 120 60" className="w-full h-14" fill="none">
        <circle cx="15" cy="30" r="8" className="fill-emerald-500/30 stroke-emerald-400" strokeWidth="1.5" />
        <text x="15" y="34" textAnchor="middle" className="fill-emerald-300 text-[10px]">A</text>
        <line x1="23" y1="25" x2="47" y2="12" className="stroke-emerald-400/60" strokeWidth="1.5" />
        <line x1="23" y1="35" x2="47" y2="48" className="stroke-emerald-400/60" strokeWidth="1.5" />
        <circle cx="55" cy="12" r="8" className="fill-emerald-500/30 stroke-emerald-400" strokeWidth="1.5" />
        <text x="55" y="16" textAnchor="middle" className="fill-emerald-300 text-[10px]">B</text>
        <circle cx="55" cy="48" r="8" className="fill-emerald-500/30 stroke-emerald-400" strokeWidth="1.5" />
        <text x="55" y="52" textAnchor="middle" className="fill-emerald-300 text-[10px]">C</text>
        <line x1="63" y1="12" x2="87" y2="25" className="stroke-emerald-400/60" strokeWidth="1.5" />
        <line x1="63" y1="48" x2="87" y2="35" className="stroke-emerald-400/60" strokeWidth="1.5" />
        <circle cx="95" cy="30" r="8" className="fill-emerald-500/30 stroke-emerald-400" strokeWidth="1.5" />
        <text x="95" y="34" textAnchor="middle" className="fill-emerald-300 text-[10px]">D</text>
      </svg>
    ),
  },
  {
    value: 'routing',
    icon: (
      <svg viewBox="0 0 120 60" className="w-full h-14" fill="none">
        <circle cx="15" cy="30" r="8" className="fill-purple-500/30 stroke-purple-400" strokeWidth="1.5" />
        <text x="15" y="34" textAnchor="middle" className="fill-purple-300 text-[10px]">A</text>
        <line x1="23" y1="30" x2="47" y2="30" className="stroke-purple-400/60" strokeWidth="1.5" />
        <rect x="47" y="20" width="20" height="20" rx="3" className="fill-purple-500/30 stroke-purple-400" strokeWidth="1.5" transform="rotate(45 57 30)" />
        <text x="57" y="34" textAnchor="middle" className="fill-purple-300 text-[10px]">?</text>
        <line x1="67" y1="22" x2="87" y2="12" className="stroke-purple-400/60" strokeWidth="1.5" />
        <line x1="67" y1="38" x2="87" y2="48" className="stroke-purple-400/60" strokeWidth="1.5" />
        <circle cx="95" cy="12" r="8" className="fill-purple-500/30 stroke-purple-400" strokeWidth="1.5" />
        <text x="95" y="16" textAnchor="middle" className="fill-purple-300 text-[10px]">B</text>
        <circle cx="95" cy="48" r="8" className="fill-purple-500/30 stroke-purple-400" strokeWidth="1.5" />
        <text x="95" y="52" textAnchor="middle" className="fill-purple-300 text-[10px]">C</text>
      </svg>
    ),
  },
]

export function PatternSelector({ onSelect, selected }: PatternSelectorProps) {
  const { t } = useTranslation('workflows')

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {PATTERNS.map((pattern) => (
        <Card
          key={pattern.value}
          variant="glass"
          className={cn(
            'p-6 cursor-pointer transition-all duration-200 hover:scale-[1.02]',
            selected === pattern.value
              ? 'ring-2 ring-indigo-500 bg-indigo-500/10'
              : 'hover:bg-white/5'
          )}
          onClick={() => onSelect(pattern.value)}
        >
          <div className="flex flex-col items-center gap-4">
            <div className="w-full px-2">{pattern.icon}</div>
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-100">
                {t(`patterns.${pattern.value}.name`)}
              </h3>
              <p className="text-sm text-gray-400 mt-1">
                {t(`patterns.${pattern.value}.description`)}
              </p>
            </div>
          </div>
        </Card>
      ))}
    </div>
  )
}
